import DBO_FWC from '@op/hooks/useFrontWebConfig';

import { PromptListType, TPromptListMap } from '../types';
import { LocaleType } from '@op/types/common';
import { FrontWebConfigKeyType } from '@op/hooks/useFrontWebConfig/defaultConfig';
import { useMemo } from 'react';

type ParamsType = { [key in 'key' | 'value']: string }[];

function replacePromptText(text: string, params: ParamsType) {
  return params.reduce((pre, cur) => {
    return pre.replaceAll(`#{${cur.key}}`, cur.value);
  }, text);
}

function replacePromptTextList(
  item: { [key in LocaleType]: string },
  params: ParamsType,
) {
  const keys: LocaleType[] = ['en-US', 'zh-CN'];
  return keys.reduce((pre, key: LocaleType) => {
    return {
      ...pre,
      [key]: replacePromptText(item[key], params),
    };
  }, {} as { [key in LocaleType]: string });
}

function getPromptWebConfigList(
  list: ({ [key in LocaleType]: string } & {
    name?: { [key in LocaleType]: string };
    mode?: 'basic' | 'advance';
  })[],
  params: ParamsType,
) {
  return list.map((item, index) => {
    const prompts = replacePromptTextList(item, params);
    const defaultName = `Default ${index + 1}`;
    return {
      ...prompts,
      name: item.name || {
        'en-US': defaultName,
        'zh-CN': defaultName,
      },
      mode: item.mode || 'basic',
      value: index + 1,
    };
  });
}

function getArrayStorePrompt(
  data: any = {},
  aiPromptList: string[],
  params: ParamsType,
) {
  const list: any[] = aiPromptList
    .map((key: string) => {
      return data[key];
    })
    .filter((item: any) => Boolean(item));

  return getPromptWebConfigList(list, params);
}

function getStorePrompts(key: FrontWebConfigKeyType, params: ParamsType) {
  const obj = DBO_FWC.get([{ key }, { key: 'ai_prompt_list' }]);

  const aiPromptList = obj?.[key] || [];

  if (Array.isArray(aiPromptList)) {
    return getArrayStorePrompt(obj?.ai_prompt_list, aiPromptList, params);
  }

  return Object.keys(aiPromptList).reduce((pre, key) => {
    return {
      ...pre,
      [key]: getArrayStorePrompt(
        obj?.ai_prompt_list,
        aiPromptList[key],
        params,
      ),
    };
  }, {} as TPromptListMap);
}

export function getDefaultPromptList(gameInfo: {
  entity_name: string;
}): PromptListType {
  return getStorePrompts('ai_default_prompt', [
    { key: 'gameName', value: gameInfo?.entity_name },
  ]);
}

export function useTopicPromptList({ topic }: { topic: string }) {
  return (gameInfo: { entity_name: string }) => {
    const params = [
      { key: 'gameName', value: gameInfo?.entity_name },
      { key: 'topic', value: topic },
    ];
    const data = getStorePrompts('topic_prompt', params);
    return data;
  };
}

export function useKeyOpinionPromptList({
  gameInfo,
}: {
  gameInfo: { entity_name: string };
}) {
  const data = useMemo(() => {
    const params = [{ key: 'gameName', value: gameInfo?.entity_name }];
    const data = getStorePrompts('key_opinion_prompt', params);

    return data;
  }, [gameInfo?.entity_name]);

  return data;
}

export function getNIKKEPromptList({
  gameInfo,
  topic,
}: {
  gameInfo: { entity_name: string };
  topic: string;
}) {
  return getStorePrompts('nikke_default_prompt', [
    { key: 'gameName', value: gameInfo?.entity_name },
    { key: 'topic', value: topic },
  ]);
}

export function getVideoPromptList() {
  return getStorePrompts('video_default_prompt', []);
}

export function getVideoPagePromptList(key: FrontWebConfigKeyType) {
  return getStorePrompts(key, []);
}

export function getPublicationsPromptList({
  gameInfo,
}: {
  gameInfo: { entity_name: string; genre: string };
}) {
  return getStorePrompts('publications_prompt', [
    { key: 'gameName', value: gameInfo?.entity_name },
    { key: 'gameGenre', value: gameInfo?.genre },
  ]);
}

export function useCustomAIPromptList({
  gameInfo,
}: {
  gameInfo: { entity_name: string };
}) {
  const data = useMemo(() => {
    const params = [{ key: 'gameName', value: gameInfo?.entity_name }];
    const data = getStorePrompts('custom_ai_prompt', params);

    return data;
  }, [gameInfo?.entity_name]);

  return data;
}
